import {useRequiredConfig} from '@domain/shifthandover'
import {SimpleDateTimePicker} from '@hconnect/common/components/shiftEventFormFields'
import {REGULAR_SIZE} from '@hconnect/common/consts'
import {Schedule} from '@hconnect/common/types'
import {getRequiredShift} from '@hconnect/common/utils'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {Box, Grid, Switch} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'

type ScheduleSwitchProps<T> = {
  label: string
  item: Partial<T>
  onChange: (value?: Schedule) => void
  errorText?: string
  disabled?: boolean
  plantId: string
}

export function ScheduleSwitch<T extends {schedule?: Schedule}>({
  label,
  item,
  onChange,
  errorText,
  disabled,
  plantId
}: ScheduleSwitchProps<T>) {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const config = useRequiredConfig(plantId)
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const currentShift = getRequiredShift(getPlantNow(), config.shifts)
  const startShift = currentShift.startDate

  const onToggle = () => {
    if (item.schedule) {
      onChange(undefined)
    } else {
      onChange({
        startDate: startShift,
        endDate: undefined
      })
    }
  }

  return (
    <>
      <Grid item xs={12}>
        <Box ml={2} mt={1}>
          {label}
          <Switch
            data-test-id="schedule-event-switch"
            color="primary"
            checked={!!item.schedule}
            onChange={onToggle}
            disabled={disabled}
          />
        </Box>
      </Grid>
      {!!item.schedule && (
        <>
          <Grid item {...REGULAR_SIZE}>
            <SimpleDateTimePicker
              label={t('shiftEvent.label.startDate')}
              date={item.schedule.startDate ? item.schedule.startDate : startShift}
              required={true}
              handleDateChange={(date) => {
                if (date) {
                  onChange({
                    startDate: date,
                    endDate: item.schedule?.endDate
                  })
                }
              }}
              variant="filled"
              disabled={disabled}
              timezone={timezone}
            />
          </Grid>
          <Grid item {...REGULAR_SIZE}>
            <SimpleDateTimePicker
              label={t('shiftEvent.label.endDate')}
              date={item.schedule.endDate ? item.schedule.endDate : null}
              required={!!item.schedule}
              errorText={errorText}
              handleDateChange={(date) => {
                if (item.schedule) {
                  onChange({
                    startDate: item.schedule.startDate,
                    endDate: date || undefined
                  })
                }
              }}
              disabled={disabled}
              timezone={timezone}
            />
          </Grid>
        </>
      )}
    </>
  )
}
